import { Box, Button, Card, Flex, Grid, GridItem, Heading, Select, Text } from '@chakra-ui/react';
import { BsPlusLg as PlusIcon } from "react-icons/bs";
import { IoEyeOutline as EyeIcon } from "react-icons/io5";
import { MdOutlineModeEditOutline as EditIcon } from "react-icons/md";
import { FaRegTrashAlt as DeleteIcon } from "react-icons/fa";
import { TopLink } from '../';
import { useState, useEffect } from 'react';
import { openAtom, modalAtom, notificationAtom, openNAtom } from '../../../../context';
import { useAtom } from 'jotai';
import ViewSetting from '../Modal/ViewSetting';
import apiCall from '../../../../utils/apiCall';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { Formik, Field, Form } from "formik";
import ReactPaginate from 'react-paginate';
import styled from '@emotion/styled';
import { format } from 'date-fns';

type FeeDataType = {
  Id: number;
  RoleName: string;
  PermissionName: string;
  ActionType: string;
  CreatedDate: string;
}

const actionTypeNames: Record<number, string> = {
  1: "Application",
  2: "Membership",
  3: "Examination",
  4: "Evoting",
};

const RolePermission = () => {
  const [, set_open] = useAtom(openAtom);
  const [, set_modal] = useAtom(modalAtom);
  const [, set_open_notification] = useAtom(openNAtom);
  const [, set_notification] = useAtom(notificationAtom);
  const queryClient = useQueryClient();

  const { data: roleList } = useQuery(['roleType'], {
    queryFn: async () =>
      await apiCall().get(`/MembershipSetUp/member/GetAllRolePermission`),
    select(data) {
      return data.data.Data?.filter(
        (item: any) => item.ActionType === 1 || item.ActionType === 3
      );
    },
  });
  
  

  const [filter_data, set_filter_data] = useState<any>(roleList);
  const itemsPerPage = 50;
  const [itemOffset, setItemOffset] = useState(0);
  const endOffset = itemOffset + itemsPerPage;
  const currentItems = filter_data?.slice(itemOffset, endOffset);
  const pageCount = Math.ceil(filter_data?.length / itemsPerPage);

  const handlePageClick = (event: any) => {
    const newOffset = (event.selected * itemsPerPage) % filter_data?.length;
    setItemOffset(newOffset);
  };

  useEffect(() => {
    set_filter_data(roleList);
  }, [roleList]);

  const { mutate: deleteFee, isLoading: isloadingDelete } = useMutation({
    mutationKey: ['delete-role-permission'],
    mutationFn: async (Id: number) => {
      console.log("Deleting Role Permission with ID:", Id); // Debugging
      return await apiCall().delete(`/MembershipSetUp/member/DeleteRolePermission/${Id}`);
    },
    onSuccess: (data: any) => {
      set_notification({
        type: 'success',
        message: data?.data?.Message || 'Role Permission deleted successfully'
      });
      set_open_notification(true);
      queryClient.invalidateQueries({ queryKey: ['rolepermission'] });
    },
    onError(error: any) {
      console.error("Delete error:", error); // Debugging
      set_notification({
        type: 'error',
        message: error?.response?.data?.Message || 'Error Occurred'
      });
      set_open_notification(true);
    },
    onSettled() {
      set_open(false);
    }
  });
  

  const handleDeleteFee = (Id: number) => {
    set_modal({
      header: 'Delete Role Permission',
      content: (
        <Flex flexDir="column" gap="20px">
          <Text>
            Are you sure you want to delete this Role Permission?
          </Text>
          <Flex p="10px 0" justify="space-between" align="center">
            <Button onClick={() => set_open(false)} variant="outline" borderColor={'var(--dark-green-color)'} 
              color="var(--dark-green-color)">Cancel</Button>
            <Button onClick={() => deleteFee(Id)} bg="var(--dark-green-color)" color="var(--white-color)">
              {isloadingDelete ? 'Loading' : 'Delete'}
            </Button>
          </Flex>
        </Flex>
      )
    });
    set_open(true);
  }

  const openAdd = (fee: FeeDataType | null) => {
    console.log("Opening modal with fee data:", fee); // Debugging
    set_modal({
      header: fee?.Id ? 'Update Role Permission' : 'Add Role Permission',
      content: (
        <AddType 
          Id={fee?.Id || null}

            RoleId={ fee?.RoleName || ''} // Check if RoleName is properly set
            PermissionId={fee?.PermissionName || ''} 
          
        />
      )
    });
    set_open(true);
  };
  

  const openView = (fee: FeeDataType) => {
    set_modal({
      header: 'View Role Permission SetUp',
      content: <ViewType fee={fee} />, // Pass the fee object
    });
    set_open(true);
  };

  return (
    <Box>
      <TopLink />
      <Flex justify="space-between" align="center" mt="20px">
        <Heading m="0" fontSize="1.5rem" color="var(--dark-green-color)">Role Permission Setup</Heading>
        <Button bg="var(--dark-green-color)" color="var(--white-color)" onClick={() => openAdd(null)} leftIcon={<PlusIcon />}>
          Add Role Permission
        </Button>
      </Flex>
      <Flex mt="25px" flexDir="column" gap="10px">
        <Heading m="0" fontSize="1rem">All Role Permission SetUp</Heading>
        <Text m="0" fontSize=".8rem">Showing {itemOffset + 1} - {Math.min(itemOffset + itemsPerPage, filter_data?.length)} of {filter_data?.length}</Text>
      </Flex>
      <Grid templateColumns={'repeat(1,.8fr)'} gap="20px" mt="20px">
  {roleList && roleList.length > 0 ? roleList.map(({ Id, RoleName, PermissionName, CreatedDate, ActionType }: FeeDataType) => 
    <Card key={Id} as={GridItem} p="20px" display="flex" gap="10px" justify="space-between" flexDir="row">
      <Flex flexDir="column" gap="3px">
        <Text><strong>Role:</strong> {RoleName}</Text>
        <Text><strong>Permission Name:</strong> {PermissionName}</Text>
        <Text><strong>Portal Type:</strong> {actionTypeNames[Number(ActionType)] || "Unknown"}</Text>
        <Text><strong>Created Date:</strong> {CreatedDate ? format(new Date(CreatedDate + "Z"), "dd/MM/yyyy") : "N/A"}</Text>
      </Flex>
      <Flex alignSelf="center" gap="10px" align="center" fontSize="1.2rem">
        <EyeIcon onClick={() => openView({ Id, RoleName, PermissionName, CreatedDate, ActionType })} style={{ cursor: 'pointer' }} />
        <EditIcon 
          onClick={() => openAdd({ 
            Id, 
            RoleName, 
            PermissionName, 
            CreatedDate, 
            ActionType 
          })} 
          style={{ cursor: 'pointer' }} 
        />
        <DeleteIcon onClick={() => handleDeleteFee(Id)} style={{ cursor: 'pointer' }} />
      </Flex>
    </Card>
  ) : ''}
</Grid>
      <StylePaginateBox>
        <ReactPaginate
          breakLabel="..."
          nextLabel=">"
          onPageChange={handlePageClick}
          pageRangeDisplayed={5}
          pageCount={pageCount}
          previousLabel="<"
          renderOnZeroPageCount={null}
        />
      </StylePaginateBox>
    </Box>
  );
};

const StylePaginateBox = styled.div`
  width: 100%;
  & > ul {
    list-style-type: none;
    text-align: center;
    padding: 0;
    margin: 0;
    margin-top: 10px;
  } 
  & > ul > li {
    display: inline;
    margin: 0 5px;
    padding: 2px 5px;
    border-radius: 3px;
    background-color: transparent;
    color: var(--dark-green-color);
  }
  & > ul > li.selected {
    background-color: var(--highlight-paginate-background);
    color: var(--highlight-paginate-color);
  }
  & > ul > li:first-of-type,
  & > ul > li:last-of-type {
    font-weight: bold;
  }
`;

const ViewType = ({ fee }: { fee: FeeDataType }) => (
  <ViewSetting>
    <Flex justify="space-between"><strong>Role:</strong> {fee?.RoleName}</Flex>
    <Flex justify="space-between"><strong>Permission Name:</strong> {fee?.PermissionName}</Flex>
    <Flex justify="space-between"><strong>Portal Type:</strong> {actionTypeNames[Number(fee?.ActionType)] || "Unknown"}</Flex>
    <Flex justify="space-between"><strong>Created Date:</strong> {fee?.CreatedDate ? format(new Date(fee.CreatedDate), "dd/MM/yyyy") : "N/A"}</Flex>
   </ViewSetting>
);

const AddType = ({ Id, RoleId, PermissionId }: any) => {
 
  const queryClient = useQueryClient();
  const [, set_open] = useAtom(openAtom);
  const [, set_open_notification] = useAtom(openNAtom);
  const [, set_notification] = useAtom(notificationAtom);

  const { mutate: addUpdate, isLoading } = useMutation({
    mutationKey: ['newPermission'],
    mutationFn: async (data: any) => {
      return await apiCall().post(`/MembershipSetUp/member/CreateUpdateRolePermission`, data);
    },
    onSuccess: (data: any) => {
      set_notification({
        type: 'success',
        message: data.data.Message || 'Permission Added Successfully'
      });
      set_open_notification(true);
      queryClient.invalidateQueries(['feeType']);
    },
    onError: (error: any) => {
      set_notification({
        type: 'error',
        message: error.data.Message || 'Error Occurred'
      });
      set_open_notification(true);
    },
    onSettled: () => {
      set_open(false);
    }
  })

  const { data: allRoles, isLoading: loadingRoles } = useQuery(['roles-list'], {
    queryFn: async () => {
      const response = await apiCall().get(`/Administration/roles/readall`);
      console.log("Roles Data:", response.data);  // Check API response in the console
      return Array.isArray(response.data) ? response.data : [];  // Ensure it's an array
    },
  });

  const { data: allPermissions, isLoading: loadingPermissions } = useQuery(
    ['admin-permissions'],
    async () => {
      const response = await apiCall().get(`/MembershipSetUp/member/GetAllPermission`);
      console.log("Permissions Data:", response.data);
      // Only keep permissions where ActionType is 1 or 3
      return response.data.Data.filter((permission: any) => 
        permission.ActionType === 1 || permission.ActionType === 3
      );
    }
  );
  
  

  return (
    <>
     <Formik
        initialValues={{
          Id: Id || null,
          RoleId: RoleId || '',
          PermissionId: PermissionId || '',
        }}
        enableReinitialize
        onSubmit={(values) => {
          console.log("Submitting values:", values); 
          const payload = { ...values };
          if (!payload.Id) {
            delete payload.Id;
          }
          addUpdate(payload);
        }}
      >


      {({ errors }) => (
          <Form>
            <Flex flexDir={"column"} align={"center"} gap="20px">
              <Box w="100%">
                Select Admin Role <br />
                <Field name="RoleId">
                {({ field }: any) => (
                  <Select {...field} borderColor="green">
                    {loadingRoles ? (
                      <option disabled>Loading roles...</option>
                    ) : allRoles && allRoles.length > 0 ? (
                      allRoles.map((role: any) => (
                        <option key={role.Id} value={String(role.Id)}>
                          {role.Name}
                        </option>
                      ))
                    ) : (
                      <option disabled>No roles available</option>
                    )}
                  </Select>
                )}
              </Field>

              </Box>

              <Box w="100%">
                Select Permission Type <br />
                <Field name="PermissionId">
                {({ field }: any) => (
                  <Select {...field} borderColor="green">
                    {loadingPermissions ? (
                      <option>Loading permissions...</option>
                    ) : allPermissions && allPermissions.length > 0 ? (
                      allPermissions.map((permission: any) => (
                        <option key={permission.Id} value={permission.Id}>
                          {permission.Name}
                        </option>
                      ))
                    ) : (
                      <option disabled>No permissions available</option>
                    )}
                  </Select>
                )}
              </Field>

              </Box>

              <Button type="submit" color="var(--white-color)" bg="var(--dark-green-color)" disabled={isLoading}>
                {isLoading ? "Loading" : Id ? 'Update' : 'Create'}
              </Button>
            </Flex>
          </Form>
        )}
      </Formik>
    </>
  );
};

export default RolePermission;